// src/components/ResultCTA.js
//
// Shared "next step" card for calculator and triage result pages.
// Locked design (see CTA rebuild discussion):
//   - One primary CTA: build whichever free product hasn't been done yet,
//     or the full assessment once both are done.
//   - One secondary, visually distinct CTA: connect with an advisor.
//     Real and direct — not hedged, not a tiny text link — but not
//     equal visual weight to the primary action.
//
// Scope note: this component only covers the plain/default case (no
// partner-linked flow, no advisor connect-form rendering outside its own
// modal). TriageResults.js's partner-linked variants are a separate,
// existing surface this does not replace.
//
// Central copy metadata lives here (NEXT_STEP_COPY, ADVISOR_FORM_COPY) so
// calculator and triage pages both import from one source instead of
// redefining "what's the next step" / advisor-form copy twice.
//
// Advisor-connect modal (new): previously each result page duplicated an
// AdvisorConnectSection component plus a local advisorState useState, and
// wired onAdvisor into ResultCTA just to flip that state — 6 near-identical
// call sites across the two pages. That's now folded entirely into this
// component: ResultCTA owns the modal, the form, and (for anonymous users)
// an OTP + claim-session step before submitting, so a fresh advisor lead
// is tied to a real account and tax_subject from the moment it's created,
// not just to a bare session. Parent pages no longer need to import
// AdvisorConnectForm, track advisor state, or pass an onAdvisor callback.
//
// SESSION LIFECYCLE (draft/publish model): calculator_sessions rows are no
// longer published on the first calculate — public_token stays null until
// an explicit publish action happens (see CalculatorResult.js's
// OwnedSidebar "Save & get link" button, and PublicSidebar's email-capture
// flow). The "Copy a link to this result" minor tier below is a pure
// client-side action with no backend call — it can't publish anything on
// demand (onSaveEmail below is dead/paused code, confirmed unused in this
// file). So it must not offer a link at all until publicToken is actually
// set, or it builds a URL to nothing (previously safe only because the old
// model minted public_token immediately on every calculation).
//
// sessionId (new): claim-session needs some way to find an unpublished
// draft (no public_token yet) when an anonymous user signs in via the
// primary "Save and continue" OTP flow below — publicToken alone can't do
// that for a draft. sessionId is the calculator session's internal id,
// passed through so claimSession() can fall back to it. Triage callers
// and already-published calculator sessions can leave this undefined;
// publicToken alone still works for them.
//
// Partner-data-share consent (new): claimSession() now accepts an
// optional consentPartnerShare flag, but it is ONLY ever passed from
// handleAdvisorOtpSuccess below — the one call site where the person has
// just been shown AdvisorConnectForm's disclosure paragraph ("you
// authorise HolaSam to share your answers... with {partner}") before this
// claim runs. handleOtpSuccess (the plain "save and continue" flow) never
// passes it, since a partner-linked anonymous user can't actually reach
// that flow in the first place — isPartnerLinked && !isAuthenticated
// always renders the early-return advisor-primary branch below, whose
// only path into claim-session is through the advisor form + OTP. No new
// checkbox was added here; the existing disclosure the person already
// sees before this exact call is the consent action being recorded.

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getKnownEmail } from '../lib/productProgress'
import { claimSession } from '../lib/claimSession'
import { submitAdvisorConnect } from '../lib/advisorConnect'
import { getAdvisorConnectionStatus, ADVISOR_CONNECTION, NO_ADVISOR_CONNECTION } from '../lib/advisorConnectionStatus'
import OtpAuth from './OtpAuth'
import ModalShell from './ModalShell'
import AdvisorConnectForm from './AdvisorConnectForm'
import MarketingConsentCheckbox from './MarketingConsentCheckbox'
import posthog from '../lib/posthog'

// ── Central copy metadata ───────────────────────────────────────────────────
// Keyed by which product produced this result and whether the *other* free
// product has already been completed. Both calculator and triage result
// pages read from this single map.

const NEXT_STEP_COPY = {
  calculator: {
    headline:         'Get your full Spain tax snapshot',
    body:             "Your IRPF estimate shows the number. The Spain snapshot gives you the bigger picture.",
    buttonLabel:      'Save and continue to Snapshot →',
    destinationLabel: 'your Spain snapshot',
    to:               '/spainsnapshot',
  },
  triage: {
    headline:         'Estimate what you may owe Spain',
    body:             "Your snapshot gives you the bigger picture. The calculator gives you a Spanish tax estimate based on your income, deductions, region, and filing facts.",
    buttonLabel:      'Save and go to Calculator →',
    destinationLabel: 'the calculator',
    to:               '/calculator',
  },
  both: {
    headline:         'Build your full US/Spain assessment',
    body:             "You've completed the free snapshot and IRPF estimate. The full assessment connects the pieces: US filing obligations, Spain filing posture, deadlines, and professional review areas.",
    buttonLabel:      'Build my full assessment →',
    destinationLabel: 'your full assessment',
    to:               '/assessment',
  },
}

// The full assessment product is temporarily disabled sitewide (mirrors
// the same gate in IntelligencePage.js, which currently hides its
// assessment card behind a hardcoded `1==0`). While this is false, a user
// who's completed both free products must not be offered "Build my full
// assessment" as their next step — it points at a product that's
// currently unreachable/hidden elsewhere in the app. NEXT_STEP_COPY.both
// is left in place rather than deleted, since this is a temporary state,
// not a permanent removal — flip this back to true (and remove the
// bothDoneAssessmentUnavailable branch below, or just let it go unused)
// once the assessment product is re-enabled.
const ASSESSMENT_ENABLED = false

// Advisor-connect form copy, keyed by the *actual* originating product
// (not the copyKey above — a "both done" result still connects an advisor
// about the specific product's session). Previously passed down as
// separate answersLabel/itemLabel props from each result page; centralized
// here since ResultCTA now owns the advisor form entirely.
const ADVISOR_FORM_COPY = {
  calculator: { answersLabel: 'calculator', itemLabel: 'estimate' },
  triage:     { answersLabel: 'triage',     itemLabel: 'snapshot' },
}

// Advisor CTA / connection copy — one source for every branch below.
// Previously each of the four render branches hand-wrote its own banner
// and button strings, and they had drifted ("✓ Sent to X…" vs "✓ You're
// connected with X…", partner-named vs generic button labels for the same
// situation). The connected banner now says what consent actually means:
// partner_data_share consent is keyed subject + partner, so the partner
// can see this person's answers — all of them, not just this result —
// matching PARTNER_CONSENT_TEXT in _shared/consentEvents.ts.
const ADVISOR_CTA_COPY = {
  connectedBanner:         (partner) => `✓ You're connected with ${partner}. They can see your answers and will follow up directly.`,
  connectedDraftBanner:    (partner) => `✓ Connected with ${partner}. This estimate is still a draft.`,
  // Shown instead of connectedBanner/connectedDraftBanner/justConnected
  // whenever advisorStatus.isDefaultPartner is true — the subject ended up
  // with the internal placeholder partner (DEFAULT_PARTNER_SLUG in
  // _shared/partnerAssignment.ts), not a named partner, so no partner name
  // is safe to print (John's copy, verbatim).
  defaultPartnerConnected: '✓ Thank you! You will be connected with a trusted partner shortly. Stay tuned.',
  publishLabel:            'Save this estimate →',
  publishingLabel:         'Saving…',
  publishError:            "Couldn't save your estimate. Please try again.",
  justConnected:           (partner) => `✓ Connected. ${partner} will be in touch shortly.`,
  sendTo:                  (partner) => `Send to ${partner} →`,
  connectGeneric:          'Connect with an advisor →',
  secondaryLabel:          'Ready to talk to a person?',
  secondaryBodyNamed:      (partner) => `${partner} can review your situation.`,
  secondaryBodyGeneric:    'Connect with an advisor who can review your situation.',
  privacy:                 '🔒 Private by default. We never share your information without your consent.',
}

// The advisor action slot, shared by all four render branches below.
// Module-level (not defined inside ResultCTA) so its component type is
// stable across renders. Connected → the banner. Otherwise → the button
// that opens the advisor-connect form, optionally preceded by lead-in
// content (the default branch's "Ready to talk to a person?" block).
// Only CONNECTED shows the banner — a REFERRED user still needs this
// button, because submitting the form is the consent action.
function AdvisorAction({
  isConnected, isDraft = false, isDefaultPartner = false, connectedPartner, buttonLabel, buttonClassName, onClick,
  onPublish = null, publishing = false, publishError = null, children,
}) {
  if (isConnected && isDraft) {
    // Connected (consent) but the calculator result on screen is an
    // unpublished draft, so advisors can't read it yet. Don't promise
    // visibility; offer an explicit Save (publish).
    return (
      <div className="u-stack-xs">
        <div className="rcta-connected-banner">
          {isDefaultPartner ? ADVISOR_CTA_COPY.defaultPartnerConnected : ADVISOR_CTA_COPY.connectedDraftBanner(connectedPartner)}
        </div>
        {onPublish && (
          <button className="btn-primary rcta-btn-full" onClick={onPublish} disabled={publishing}>
            {publishing ? ADVISOR_CTA_COPY.publishingLabel : ADVISOR_CTA_COPY.publishLabel}
          </button>
        )}
        {publishError && <div className="otp-error">{publishError}</div>}
      </div>
    )
  }
  if (isConnected) {
    return (
      <div className="rcta-connected-banner">
        {isDefaultPartner ? ADVISOR_CTA_COPY.defaultPartnerConnected : ADVISOR_CTA_COPY.connectedBanner(connectedPartner)}
      </div>
    )
  }
  const button = (
    <button className={`${buttonClassName} rcta-btn-full`} onClick={onClick}>
      {buttonLabel}
    </button>
  )
  if (!children) return button
  return <div className="u-stack-xs">{children}{button}</div>
}


// product: 'calculator' | 'triage' — which result page this is rendered on
// publicToken: the session's public_token — needed to call claim-session
//   once OTP verification succeeds, and required (non-null) for the
//   copy-link minor tier to render at all — see lifecycle note above.
// otherProductDone: bool — has the user already completed the other free product
// isAuthenticated: bool
// onSaveEmail: (email) => Promise<void> — PAUSED, not currently called.
//   Was the minor-tier mechanism before Phase 3 was paused (see copyState
//   below). Still accepted as a prop so the parent result pages don't need
//   a signature change when Phase 3 resumes — just currently unused.
// isPartnerLinked / partnerName: when a session carries a partner_ref
//   (arrived via a partner's ?ref= link). For anonymous users this flips
//   which action is primary — the partner-advisor path becomes primary and
//   the product CTA drops to secondary. For authenticated users it only
//   personalizes the secondary advisor copy; the product CTA stays primary.
//   isPartnerLinked also skips the advisor-connect OTP step (see
//   handleAdvisorCollected) — a ?ref= visitor already has a real
//   relationship with that partner, adding auth friction there doesn't
//   protect anything the ref link doesn't already establish.
// isPublished: bool, default true. False only for an unpublished calculator
//   draft; then a connected user sees the draft banner + Save button instead
//   of "they can see your answers" (advisor reads require published_at).
//   Defaults to true because triage sessions are always published on submit,
//   so triage callers pass nothing. A new caller that forgets this prop gets
//   the normal connected banner.
// onPublish: async () => void, throws on failure. Publishes the current
//   draft via the parent's handlePublish (so hook state stays in sync).
//   Only used with isPublished={false}.
// userEmail: the authenticated user's account email. Passed into the
//   advisor-connect form pre-filled and locked (see AdvisorConnectForm.js)
//   so an authenticated submission's contact_email always matches the
//   account it's tied to.
// The session's advisor relationship ('none' | 'referred' | 'connected',
// see advisorConnectionStatus.js) is resolved internally (see
// getAdvisorConnectionStatus / advisorStatus below) rather than passed in
// as a prop. 'referred' (partner_id set by a silent referral claim, no
// consent yet) must NOT render as connected — the Send button is the
// consent action. It used to be each result page's job to fetch this
// once on load and remember to re-fetch it after a same-session
// submission; only TriageResults.js ever got the refetch wiring, which is
// exactly how CalculatorResult.js ended up with stale CTA state. Owning it
// here means neither page (nor any future one) has to implement this at
// all.

export default function ResultCTA({
  product,
  publicToken,
  sessionId = null,
  otherProductDone = false,
  isAuthenticated = false,
  onSaveEmail,
  isPartnerLinked = false,
  partnerName = null,
  isUsPerson = null,
  userEmail = null,
  isPublished = true,
  onPublish = null,
}) {
  const navigate = useNavigate()

  const [publishing, setPublishing]     = useState(false)
  const [publishError, setPublishError] = useState(null)
  const handlePublishClick = async () => {
    setPublishing(true)
    setPublishError(null)
    try {
      await onPublish()
    } catch (err) {
      setPublishError(ADVISOR_CTA_COPY.publishError)
    } finally {
      setPublishing(false)
    }
  }

  // Primary tier — real auth via OTP.
  const [showOtp, setShowOtp]   = useState(false)
  const [claiming, setClaiming] = useState(false)
  // True once claim-session has finished (success or non-fatal failure)
  // after OTP verification. Replaces the old auto-redirect — see
  // handleOtpSuccess/goToNextProduct.
  const [otpClaimed, setOtpClaimed] = useState(false)
  // BUG FIX: handleOtpSuccess used to ignore claimSession()'s return value
  // entirely and set otpClaimed=true unconditionally in a finally block —
  // meaning a failed claim (e.g. a draft session with no public_token,
  // before the session_id fallback existed) still showed "✓ You're signed
  // in and saved" even though owner_user_id was never actually set on the
  // row. This tracks a real failure so the UI can show it and offer a
  // retry instead of a false confirmation.
  const [claimError, setClaimError] = useState(null)

  // Marketing consent for the primary "Save and continue" OTP flow — the
  // one place both calculator and triage results funnel through when NOT
  // partner-linked (partner-linked anonymous users take the early-return
  // advisor-first branch below, whose OTP step already asks via
  // AdvisorConnectForm's own checkbox). Reuses MarketingConsentCheckbox.js
  // rather than a third hand-rolled copy. Recording the resulting value
  // against a real subject happens server-side in claim-session, once
  // claimSession() succeeds — see handleOtpSuccess/retryClaim below.
  const [marketingConsent, setMarketingConsent] = useState(false)

  // Set true the moment the primary flow successfully records a real
  // opt-in — used to hide AdvisorConnectForm's own independent checkbox
  // afterward (see handleAdvisorCollected's render site below) so the
  // person isn't asked the same question twice with no memory of the
  // first answer. This is a same-session UX convenience layered on top
  // of the real fix (advisor-connect/index.ts and claim-session/index.ts
  // both now independently refuse to overwrite an existing answer via
  // hasMarketingConsent()) — it doesn't cover a RETURNING user who
  // answered in an earlier session and is opening this form fresh today;
  // that case is still safe from data corruption (the backend guard
  // handles it), just not hidden from view. A real fix for that would
  // need a dedicated read call before this page renders the form at all —
  // flagged as a possible follow-up, not built here.
  const [hasGivenMarketingConsent, setHasGivenMarketingConsent] = useState(false)

  // Minor tier — copy-a-link action. Phase 3 (the plain transactional
  // email via Zoho SMTP) is paused, and the interim "email myself this
  // link" mechanism shared Supabase's signInWithOtp/email-template
  // infrastructure with the primary OTP flow — a real, confirmed bug
  // (changing the OTP template affected both tiers). Copy-link touches no
  // backend and no auth at all, so it can't collide with anything.
  // 'idle' | 'copied' | 'fallback' (clipboard API unavailable/denied)
  const [copyState, setCopyState] = useState('idle')

  // ── Advisor-connection status (owned internally, see advisorConnectionStatus.js) ──
  // BUG FIX: this used to pass only publicToken, which is null for any
  // unpublished draft session — meaning the connection check silently
  // could never succeed for a draft, regardless of the real
  // tax_subjects.partner_id state (see advisorConnectionStatus.js for the
  // full explanation). sessionId was already a prop on this component
  // (used by claimSession/submitAdvisorConnect below) — it just wasn't
  // being threaded into this particular check yet.
  //
  // BUG FIX (Sept 2026): was a boolean from tax_subjects.partner_id alone,
  // which conflated attribution with consent — see advisorConnectionStatus.js.
  const [advisorStatus, setAdvisorStatus] = useState(NO_ADVISOR_CONNECTION)

  const refreshConnectionStatus = useCallback(async () => {
    const next = await getAdvisorConnectionStatus(product, publicToken, sessionId)
    setAdvisorStatus(next)
  }, [product, publicToken, sessionId])

  const connectedToAdvisor = advisorStatus.status === ADVISOR_CONNECTION.CONNECTED
  const isDraftConnected = connectedToAdvisor && !isPublished
  const referredToAdvisor  = advisorStatus.status === ADVISOR_CONNECTION.REFERRED

  useEffect(() => {
    refreshConnectionStatus()
  }, [refreshConnectionStatus])

  // ── Advisor-connect modal state ─────────────────────────────────────────
  // 'closed' | 'form' | 'otp' | 'submitting' | 'done' | 'error'
  const [advisorStep, setAdvisorStep]       = useState('closed')
  const [advisorContact, setAdvisorContact] = useState(null)
  const [advisorError, setAdvisorError]     = useState(null)

  // One-time "connected" confirmation after the advisor OTP flow's hard
  // reload (see handleAdvisorOtpSuccess for why it reloads instead of just
  // flipping local state). Read once on mount, then stripped from the URL
  // so refreshing the page doesn't keep re-showing it.
  const [showConnectedBanner, setShowConnectedBanner] = useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('advisor_connected') === '1') {
      setShowConnectedBanner(true)
      params.delete('advisor_connected')
      const newSearch = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''))
    }
  }, [])

  // otherProductDone comes solely from the caller (useProductMode.js's
  // DB-backed, authenticated check) now. Previously OR'd with a
  // sessionStorage same-visit breadcrumb (isProductDone) — removed: that
  // signal fired the moment a result page was merely viewed, regardless of
  // whether the person ever saved/signed in, which meant an anonymous
  // visitor who did both products in one tab without saving either got
  // shunted into the "both done" advisor-only card below with no way to
  // save the result they were currently looking at. If someone hasn't
  // created an account, there's no real "done" to acknowledge — they
  // can't return to an unsaved result in this tab anyway, so treating it
  // as anything less than "not done" was never actually useful, just
  // occasionally wrong. See productProgress.js — markProductDone/
  // isProductDone are now unused there; getKnownEmail/saveKnownEmail
  // (a genuinely separate, still-legitimate convenience) are untouched.
  const computedOtherProductDone = otherProductDone

  // Non-US-person users who've completed both free products don't have a
  // US side to reconcile, so the "full US/Spain assessment" framing doesn't
  // fit them. Advisor becomes the sole next step instead.
  const bothDoneNonUs = computedOtherProductDone && isUsPerson === false

  // Distinct from bothDoneNonUs above on purpose, even though both branches
  // currently render the same "advisor is the only next step" shape: this
  // one is a temporary availability flag (ASSESSMENT_ENABLED), that one is
  // a permanent, correctness-driven decision about non-US persons. Keeping
  // them separate means re-enabling the assessment product later doesn't
  // risk touching the non-US-person logic, and vice versa. `!bothDoneNonUs`
  // guard avoids double-handling the same user in both branches.
  const bothDoneAssessmentUnavailable = computedOtherProductDone && !bothDoneNonUs && !ASSESSMENT_ENABLED

  const copyKey = computedOtherProductDone ? 'both' : product
  const copy    = NEXT_STEP_COPY[copyKey]
  const advisorCopy = ADVISOR_FORM_COPY[product] || ADVISOR_FORM_COPY.triage
  // partnerName (prop) comes from THIS session's partner_ref, which is
  // where advisor-connect will route a submission — so it wins for the
  // Send button. The status's partnerName is the subject's assigned
  // partner, used when the session itself carries no ref (e.g. attributed
  // on an earlier claim) and for the connected banner, which describes the
  // relationship that actually exists.
  const partnerLabel          = partnerName || advisorStatus.partnerName || 'your advisor'
  const connectedPartnerLabel = advisorStatus.partnerName || partnerLabel

  // Copy only — a partner is "named" either via this session's ?ref= or
  // via an existing referral on the subject. Flow gating (OTP skip, modal
  // submit label) still keys off isPartnerLinked / isAuthenticated as before.
  const hasNamedPartner    = isPartnerLinked || referredToAdvisor
  const advisorButtonLabel = hasNamedPartner ? ADVISOR_CTA_COPY.sendTo(partnerLabel) : ADVISOR_CTA_COPY.connectGeneric

  // One event covers all three destinations copy.to can resolve to
  // (/spainsnapshot, /calculator, /assessment) — the button/handler is
  // shared across all of them in this component, so a single event with a
  // destination property is more honest than naming it after just one
  // destination. Filter on destination === '/assessment' for purchase
  // intent specifically.
  function trackProductCtaClick() {
    posthog.capture('product_cta_clicked', { product, destination: copy.to })
  }

  // Fired at all three "open the advisor-connect form" entry points below —
  // partner-linked primary CTA, both-done-non-US primary CTA, and the
  // standard secondary CTA.
  function trackAdvisorCtaClick() {
    posthog.capture('advisor_cta_clicked', {
      product,
      is_partner_linked: isPartnerLinked,
      advisor_status:    advisorStatus.status,
    })
  }

  function openAdvisorForm() {
    trackAdvisorCtaClick()
    setAdvisorStep('form')
  }

  // Called by OtpAuth once a real session exists. Claims the session
  // (creates/updates tax_subjects, tax_profiles, tax_year_profiles via the
  // generalized claim-session function) then shows a confirmation with a
  // link to continue — no longer auto-navigates. The auto-redirect felt
  // abrupt/startling (flagged during testing); the person now chooses when
  // to move on. Claim failure is still non-fatal — matches the existing
  // pattern in AuthCallback.js of not stranding the user over a
  // non-critical write, they just see the same confirmation either way.
  //
  // No consentPartnerShare here, deliberately: this is the plain "save and
  // continue" flow, reachable only when NOT (isPartnerLinked &&
  // !isAuthenticated) — see the early-return branch below. A partner-linked
  // anonymous user can't hit this path at all, so there's no disclosure to
  // record a consent fact against here.
  async function handleOtpSuccess(session) {
    setClaiming(true)
    setClaimError(null)
    try {
      const claimed = await claimSession({ product, publicToken, sessionId, accessToken: session.access_token, marketingConsent })
      if (!claimed) {
        // Real failure — the person IS authenticated now (OTP already
        // succeeded), but the calculator/triage session never actually got
        // attached to their account. Surface this instead of the old
        // behavior of silently proceeding to a false "saved" confirmation.
        setClaimError("You're signed in, but we couldn't save this to your account just now.")
        return
      }
      setOtpClaimed(true)
      if (marketingConsent) setHasGivenMarketingConsent(true)
    } finally {
      setClaiming(false)
    }
  }

  // Retries the claim without re-running OTP — the person already has a
  // real Supabase session from the first attempt, claim-session just
  // needs to be called again with it.
  async function retryClaim() {
    setClaiming(true)
    setClaimError(null)
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      if (!authSession) {
        setClaimError('Please sign in again.')
        return
      }
      const claimed = await claimSession({ product, publicToken, sessionId, accessToken: authSession.access_token, marketingConsent })
      if (!claimed) {
        setClaimError("Still couldn't save — please try again in a moment.")
        return
      }
      setOtpClaimed(true)
      if (marketingConsent) setHasGivenMarketingConsent(true)
    } finally {
      setClaiming(false)
    }
  }

  // Hard navigation, not react-router's navigate() — deliberately kept for
  // this one transition even though it's now user-initiated rather than
  // automatic. This is the one moment where AuthContext (user, via async
  // onAuthStateChange), SubjectContext (depends on AuthContext's user), and
  // whatever page we land on (depends on SubjectContext) are all
  // initializing off a brand-new session AT THE SAME TIME claim-session
  // wrote the rows they all read. A client-side navigate() carries all
  // three pieces of in-memory state forward as-is, whatever state they
  // happen to be in — which is how stale reads kept surfacing in one file
  // after another. A full reload here throws that in-memory state away and
  // re-mounts everything fresh against data that's already committed
  // server-side, same as manually refreshing. This only affects this one
  // post-claim transition — every other navigation in the app stays a
  // normal, instant client-side transition.
  function goToNextProduct() {
    window.location.href = copy.to
  }

  // ── Advisor-connect handlers ─────────────────────────────────────────────

  // Fires the actual advisor-connect submission. Used both for the
  // skip-OTP path (already authenticated, or partner-linked) and as the
  // final step after OTP + claim-session succeed for anonymous users.
  async function submitAdvisor(contact) {
    setAdvisorError(null)
    setAdvisorStep('submitting')
    try {
      await submitAdvisorConnect({ product, publicToken, sessionId, contact })
      posthog.capture('advisor_connection_requested', {
        product,
        is_authenticated: isAuthenticated,
        is_partner_linked: isPartnerLinked,
      })
      setAdvisorStep('done')
      // No reload on this path (already authenticated / partner-linked —
      // see handleAdvisorCollected), so connectedToAdvisor would otherwise
      // stay stale until a manual refresh. Re-fetch it directly now that
      // ResultCTA owns this state itself.
      refreshConnectionStatus()
    } catch (err) {
      setAdvisorError(err.message || 'Something went wrong. Please try again.')
      setAdvisorStep('form')
    }
  }

  // AdvisorConnectForm hands up validated contact fields here. Whether we
  // submit immediately or detour through OTP first depends on whether this
  // person already has a real identity attached to the session:
  //   - isAuthenticated: already signed in, nothing to gain from OTP.
  // isPartnerLinked no longer skips OTP on its own — a ?ref= link
  // identifies which partner gets the lead, but doesn't establish who the
  // person actually is. Without OTP here, a partner-linked anonymous
  // submission never gets a tax_subject, never gets claimed, and shows up
  // in the advisor dashboard as "Awaiting Signup" with no usable profile
  // data — exactly the gap surfaced in testing. Routing through the same
  // OTP path anonymous non-partner-linked users already use fixes this
  // with no new plumbing: claim-session already assigns partner_id from
  // session.partner_ref server-side (see claim-session/index.ts step 7),
  // so nothing downstream needs to change, only this gate.
  function handleAdvisorCollected(contact) {
    setAdvisorContact(contact)
    if (isAuthenticated) {
      submitAdvisor(contact)
    } else {
      setAdvisorStep('otp')
    }
  }

  // Runs after OtpAuth verifies a brand-new session for an anonymous
  // advisor-connect submission: claim the session first (so tax_subject_id
  // exists), then submit the lead (so advisor-connect can propagate
  // partner_id onto that now-claimed tax_subject), then hard-reload.
  //
  // The reload — not just setting advisorStep('done') — exists for the
  // same reason handleOtpSuccess above uses window.location.href instead
  // of navigate(): this person just authenticated for the first time, and
  // the rest of this page (the isAuthenticated/ownerState props ResultCTA
  // was rendered with) was computed before that happened. Reloading
  // re-mounts everything against the now-committed, authenticated state
  // rather than leaving the surrounding page silently stale. The
  // advisor_connected query flag lets the reloaded page show a one-time
  // confirmation banner in place of the in-modal "done" message this
  // reload would otherwise throw away.
  //
  // consentPartnerShare: true is passed here — and ONLY here — because
  // this is the one call site where the person has just been shown
  // AdvisorConnectForm's disclosure paragraph (rendered in advisorStep ===
  // 'form', right before this OTP step) and clicked through it. This is
  // also the ONLY route by which a partner-linked anonymous user's claim-
  // session call can ever fire, since isPartnerLinked && !isAuthenticated
  // renders the early-return branch below, whose only path into OTP is
  // this same advisor-form-first flow. claim-session records the actual
  // consent fact (see claim-session/index.ts step 7b); this is just
  // telling it that the disclosure was in fact shown and agreed to.
  //
  // BUG FIX: this used to always hard-reload to
  // `${window.location.pathname}?advisor_connected=1`, unconditionally.
  // That's only safe when the current pathname is a real, addressable page
  // to reload BACK to — true for an already-published result
  // (/calculator/result/:token, /spainsnapshot/results/:publicToken) or for
  // triage (which publishes immediately, so it always has one). It is NOT
  // true for an unpublished calculator draft: /calculator is a single route
  // (see App.js) that renders every step, including the result, as
  // in-memory client state inside CalculatorPage — a draft has no
  // public_token and therefore no distinct URL of its own. Reloading
  // /calculator for a draft remounts CalculatorPage from scratch with
  // nothing to rehydrate the in-progress form or result from — the
  // "advisor OTP works, then the page silently resets to a blank
  // calculator" bug. Now the reload only happens when publicToken exists;
  // otherwise this confirms in place, exactly like the already-
  // authenticated path (submitAdvisor, above) already does successfully.
  // The one thing this trades away: isAuthenticated (from useProductMode,
  // "only computed once at mount" per CalculatorPage.js's own comment)
  // stays stale on this screen until the person's next real navigation —
  // a smaller, already-precedented imperfection (handleOtpSuccess's plain
  // flow works the same way) than destroying their draft.
  async function handleAdvisorOtpSuccess(session) {
    setAdvisorStep('submitting')
    try {
      const claimed = await claimSession({
        product,
        publicToken,
        sessionId,
        accessToken: session.access_token,
        consentPartnerShare: true,
      })
      if (!claimed) {
        // claimSession() is non-fatal by design (see claimSession.js) — it
        // never throws, it returns false and logs. That's fine for the
        // plain "next product" OTP flow, but here it silently defeated the
        // entire point of doing OTP -> claim -> advisor-connect in order:
        // advisor-connect would still fire against a session with no
        // tax_subject_id, so the lead could never get linked or have the
        // partner assignment propagated onto it. Stop and surface an error
        // instead of proceeding.
        setAdvisorError("We couldn't verify your account just now. Please try again.")
        setAdvisorStep('form')
        return
      }
      await submitAdvisorConnect({ product, publicToken, sessionId, contact: advisorContact })
      posthog.capture('advisor_connection_requested', {
        product,
        is_authenticated: false,
        is_partner_linked: isPartnerLinked,
      })
      if (publicToken) {
        // A real, addressable page exists to reload back to (published
        // calculator result, or any triage result — triage always has a
        // token). Safe to reload for fresh AuthContext/SubjectContext.
        window.location.href = `${window.location.pathname}?advisor_connected=1`
      } else {
        // Unpublished draft — no URL to reload to without wiping the form
        // (see BUG FIX note above). Confirm in place instead, same as the
        // already-authenticated submitAdvisor() path.
        setAdvisorStep('done')
        refreshConnectionStatus()
      }
    } catch (err) {
      setAdvisorError(err.message || 'Something went wrong. Please try again.')
      setAdvisorStep('form')
    }
  }

  function closeAdvisorModal() {
    setAdvisorStep('closed')
    setAdvisorError(null)
  }

  // Minor tier — copy-a-link. No backend call, no Supabase Auth — see
  // state comment above for why that matters right now.
  //
  // BUG FIX: resultUrl used to be built unconditionally from publicToken.
  // Under the old always-publish model that was safe — calculate-irpf
  // minted public_token on the very first calculation, so it was never
  // null by the time this component could render. Under the draft/publish
  // model, publicToken is legitimately null until an explicit publish
  // action happens (see CalculatorResult.js's OwnedSidebar/PublicSidebar),
  // and this component has no way to trigger that itself — onSaveEmail is
  // dead/paused code (confirmed unused below), so clicking this button
  // could never have published anything even if it tried. Result:
  // clicking it built and copied "/calculator/result/null" — a broken
  // link — and, since the action is pure client-side clipboard work,
  // published_at never got touched either. Now resultUrl/resultPath are
  // only computed when publicToken actually exists, and the whole minor
  // tier is hidden until then instead of offering a link to nothing.
  const resultPath = publicToken
    ? (product === 'calculator'
        ? `/calculator/result/${publicToken}`
        : `/spainsnapshot/results/${publicToken}`)
    : null
  const resultUrl = resultPath ? `${window.location.origin}${resultPath}` : null

  async function handleCopyLink() {
    if (!resultUrl) return
    try {
      await navigator.clipboard.writeText(resultUrl)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      // Clipboard API unavailable or permission denied — fall back to a
      // manual-copy input rather than leaving the click with no effect.
      setCopyState('fallback')
    }
  }

  const advisorModal = advisorStep !== 'closed' && (
    <ModalShell onClose={closeAdvisorModal}>
      {advisorStep === 'form' && (
        <>
          <h2 className="signin-modal-title">Connect with {partnerLabel}</h2>
          <p className="signin-modal-body">
            {partnerLabel} will follow up directly using the details below.
          </p>
          <AdvisorConnectForm
            partnerName={partnerLabel}
            answersLabel={advisorCopy.answersLabel}
            initialEmail={isAuthenticated ? (userEmail || '') : ''}
            emailLocked={isAuthenticated && !!userEmail}
            submitLabel={
              isAuthenticated || isPartnerLinked
                ? `Send ${advisorCopy.itemLabel} to ${partnerLabel} →`
                : 'Continue →'
            }
            onCollected={handleAdvisorCollected}
            hideMarketingConsent={hasGivenMarketingConsent}
          />
          {advisorError && <p className="otp-error">{advisorError}</p>}
        </>
      )}
      {advisorStep === 'otp' && (
        <>
          <h2 className="signin-modal-title">Verify your email</h2>
          <p className="signin-modal-body">
            One quick step — this creates your HolaSam account so {partnerLabel} (and you) can find
            this later.
          </p>
          <OtpAuth
            initialEmail={advisorContact?.email}
            onSuccess={handleAdvisorOtpSuccess}
            submitLabel="Connect"
          />
          {advisorError && <p className="otp-error">{advisorError}</p>}
        </>
      )}
      {advisorStep === 'submitting' && (
        <p className="signin-modal-body">Connecting you with {partnerLabel}…</p>
      )}
      {advisorStep === 'done' && (
        <div className="tr-email-sent">
          {advisorStatus.isDefaultPartner ? ADVISOR_CTA_COPY.defaultPartnerConnected : ADVISOR_CTA_COPY.justConnected(partnerLabel)}
        </div>
      )}
    </ModalShell>
  )

  // ── Partner-linked, anonymous: advisor path is primary ───────────────────
  if (isPartnerLinked && !isAuthenticated) {
    return (
      <div className="rcta-card">
        <div className="u-eyebrow">Next step</div>
        <h2 className="rcta-headline">{partnerLabel} can review your Spain-side inputs.</h2>
        <p className="rcta-body">
          {partnerLabel} can review your inputs and help you understand what to do next on the Spain side.
        </p>
        <AdvisorAction
          isConnected={connectedToAdvisor}
          isDraft={isDraftConnected}
          isDefaultPartner={advisorStatus.isDefaultPartner}
          onPublish={onPublish ? handlePublishClick : null}
          publishing={publishing}
          publishError={publishError}
          connectedPartner={connectedPartnerLabel}
          buttonLabel={advisorButtonLabel}
          buttonClassName="btn-primary"
          onClick={openAdvisorForm}
        />

        {connectedToAdvisor && (
          <>
            <div className="rcta-divider" />

            <div className="u-stack-xs">
              <p className="rcta-advisor-label">Want to understand the US side too?</p>
              <button className="rcta-btn-outline rcta-btn-full" onClick={() => { trackProductCtaClick(); navigate(copy.to) }}>
                {copy.buttonLabel}
              </button>
            </div>
          </>
        )}

        <div className="rcta-privacy">
          {ADVISOR_CTA_COPY.privacy}
        </div>
        {advisorModal}
      </div>
    )
  }

  // ── Both free products done, not a US person: advisor is the only
  // meaningful next step — there's no US side for an assessment to bridge.
  if (bothDoneNonUs) {
    return (
      <div className="rcta-card">
        <div className="u-eyebrow">Next step</div>
        <h2 className="rcta-headline">You've covered both the numbers and the details</h2>
        <p className="rcta-body">
          Your Spain snapshot and IRPF estimate cover the Spain side end to end.
          From here, a professional review is usually more useful than a
          US/Spain assessment, since your situation doesn't involve US filing.
        </p>
        <AdvisorAction
          isConnected={connectedToAdvisor}
          isDraft={isDraftConnected}
          isDefaultPartner={advisorStatus.isDefaultPartner}
          onPublish={onPublish ? handlePublishClick : null}
          publishing={publishing}
          publishError={publishError}
          connectedPartner={connectedPartnerLabel}
          buttonLabel={advisorButtonLabel}
          buttonClassName="btn-primary"
          onClick={openAdvisorForm}
        />
        <div className="rcta-privacy">
          {ADVISOR_CTA_COPY.privacy}
        </div>
        {advisorModal}
      </div>
    )
  }

  // ── Both free products done, US person, but the full assessment product
  // is temporarily disabled (ASSESSMENT_ENABLED): don't offer "Build my
  // full assessment" — it points at a product that's currently unreachable
  // elsewhere in the app (see IntelligencePage.js's matching gate). Advisor
  // becomes the next step instead, same shape as bothDoneNonUs above but
  // for a different, temporary reason.
  if (bothDoneAssessmentUnavailable) {
    return (
      <div className="rcta-card">
        <div className="u-eyebrow">Next step</div>
        <h2 className="rcta-headline">You've covered both the numbers and the details</h2>
        <p className="rcta-body">
          Your Spain snapshot and IRPF estimate are both done. The full
          US/Spain assessment isn't available right now — in the meantime,
          a professional review is the best next step.
        </p>
        <AdvisorAction
          isConnected={connectedToAdvisor}
          isDraft={isDraftConnected}
          isDefaultPartner={advisorStatus.isDefaultPartner}
          onPublish={onPublish ? handlePublishClick : null}
          publishing={publishing}
          publishError={publishError}
          connectedPartner={connectedPartnerLabel}
          buttonLabel={advisorButtonLabel}
          buttonClassName="btn-primary"
          onClick={openAdvisorForm}
        />
        <div className="rcta-privacy">
          {ADVISOR_CTA_COPY.privacy}
        </div>
        {advisorModal}
      </div>
    )
  }

  return (
    <div className="rcta-card">
      <div className="u-eyebrow">Next step</div>
      {showConnectedBanner && (
        <div className="rcta-connected-banner">
          {advisorStatus.isDefaultPartner ? ADVISOR_CTA_COPY.defaultPartnerConnected : ADVISOR_CTA_COPY.justConnected(partnerLabel)}
        </div>
      )}
      <h2 className="rcta-headline">{copy.headline}</h2>
      <p className="rcta-body">{copy.body}</p>

      {isAuthenticated ? (
        <button className="btn-primary rcta-btn-full" onClick={() => { trackProductCtaClick(); navigate(copy.to) }}>
          {copy.buttonLabel}
        </button>
      ) : otpClaimed ? (
        <div className="rcta-otp-claimed">
          <div className="rcta-connected-banner">✓ You're signed in and saved.</div>
          <button className="btn-primary rcta-btn-full" onClick={() => { trackProductCtaClick(); goToNextProduct() }}>
            Continue to {copy.destinationLabel} →
          </button>
        </div>
      ) : claiming ? (
        <div className="rcta-confirming">
          Signing you in…
        </div>
      ) : claimError ? (
        <div className="u-stack-xs">
          <p className="otp-error">{claimError}</p>
          <button className="btn-primary rcta-btn-full" onClick={retryClaim}>
            Try again →
          </button>
        </div>
      ) : showOtp ? (
        <>
          <MarketingConsentCheckbox checked={marketingConsent} onChange={setMarketingConsent} />
          <OtpAuth
            initialEmail={getKnownEmail()}
            onSuccess={handleOtpSuccess}
            submitLabel={copy.buttonLabel.replace(' →', '')}
          />
        </>
      ) : (
        <button className="btn-primary rcta-btn-full" onClick={() => { trackProductCtaClick(); setShowOtp(true) }}>
          {copy.buttonLabel}
        </button>
      )}

      <div className="rcta-divider" />

      <AdvisorAction
        isConnected={connectedToAdvisor}
        isDraft={isDraftConnected}
        isDefaultPartner={advisorStatus.isDefaultPartner}
        onPublish={onPublish ? handlePublishClick : null}
        publishing={publishing}
        publishError={publishError}
        connectedPartner={connectedPartnerLabel}
        buttonLabel={advisorButtonLabel}
        buttonClassName="rcta-btn-outline"
        onClick={openAdvisorForm}
      >
        <p className="rcta-advisor-label">{ADVISOR_CTA_COPY.secondaryLabel}</p>
        <p className="rcta-advisor-body">
          {hasNamedPartner
            ? ADVISOR_CTA_COPY.secondaryBodyNamed(partnerLabel)
            : ADVISOR_CTA_COPY.secondaryBodyGeneric}
        </p>
      </AdvisorAction>

      {publicToken && (
        <>
          <div className="rcta-divider" />
          <div className="u-stack-sm">
            {copyState === 'fallback' ? (
              <>
                <p className="rcta-minor-fallback-hint">Copy this link manually:</p>
                <input
                  type="text"
                  readOnly
                  className="app-input rcta-minor-input"
                  value={resultUrl}
                  onFocus={e => e.target.select()}
                />
              </>
            ) : (
              <button className="rcta-minor-link" onClick={handleCopyLink}>
                {copyState === 'copied' ? '✓ Link copied' : 'Copy a link to this result'}
              </button>
            )}
          </div>
        </>
      )}

      <div className="rcta-privacy">
        {ADVISOR_CTA_COPY.privacy}
      </div>
      {advisorModal}
    </div>
  )
}